import { createNPointFontScaleTheme, createTheme, defaultResolvers, defaultStylers } from '@byteclaw/visage';
export { createNPointFontScaleTheme } from '@byteclaw/visage';
import color from 'color';
import { getResponsiveValue } from '@byteclaw/visage-utils';
import ModularScale from 'modular-scale';
export { ratios as modularScaleFontRatios } from 'modular-scale';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function isContrastOk(foreground, background) {
  var contrast = foreground.contrast(background);
  return contrast >= 4.5 && contrast <= 5.0;
}
/**
 * Find a foreground color with good contrast
 */


function findForegroundColor(source, background) {
  var sourceColor = color(source);
  var backgroundColor = color(background);

  if (isContrastOk(sourceColor, backgroundColor)) {
    return sourceColor.string();
  } // reset source color


  for (var s = 100; s >= 0; s--) {
    for (var l = 0; l <= 100; l++) {
      var col = color().hue(sourceColor.hue()).saturationl(s).lightness(l);

      if (isContrastOk(col, backgroundColor)) {
        return col.string();
      }
    }
  }

  return sourceColor.string();
}

function generateColorScale(name, forColor, lightSteps, darkSteps) {
  var _ref;

  if (lightSteps === void 0) {
    lightSteps = 0;
  }

  if (darkSteps === void 0) {
    darkSteps = 0;
  }

  var col = color(forColor);
  var lghtns = col.lightness();
  var colors = [];

  for (var i = lightSteps; i > 0; --i) {
    colors.push(col.lightness(lghtns + (100 - lghtns) * (1 / (lightSteps + 1) * i)).rgb().toString());
  }

  colors.push(col.rgb().toString());

  for (var _i = 1; _i < darkSteps + 1; ++_i) {
    colors.push(col.lightness(lghtns - lghtns * (1 / (lightSteps + 1) * _i)).rgb().toString());
  }

  return _ref = {}, _ref[name] = {
    values: colors,
    offset: lightSteps
  }, _ref[name + "Text"] = {
    values: colors.map(function (bgColor) {
      return color(bgColor).isDark() ? 'white' : 'black';
    }),
    offset: lightSteps
  }, _ref;
}

var darkShades = /*#__PURE__*/color('#1b262c');
var accent = /*#__PURE__*/color('#dbe2ef');
var shades = /*#__PURE__*/color('#fff');
var primary = /*#__PURE__*/color('#1FA7EC');
var docsThemeColorPalette = /*#__PURE__*/_extends({}, /*#__PURE__*/generateColorScale('neutral', '#e3e8ee', 5, 5), {
  darkShades: /*#__PURE__*/darkShades.toString(),
  darkShadesText: /*#__PURE__*/darkShades.isDark() ? '#fff' : '#000',
  accent: /*#__PURE__*/accent.toString(),
  accentText: /*#__PURE__*/accent.isDark() ? '#fff' : '#000',
  shades: /*#__PURE__*/shades.toString(),
  shadesText: /*#__PURE__*/shades.isDark() ? '#fff' : '#000',
  primary: /*#__PURE__*/primary.toString(),
  primaryText: '#fff'
}, /*#__PURE__*/generateColorScale('success', '#27ae60', 5, 5), /*#__PURE__*/generateColorScale('danger', '#c0392b', 5, 5), /*#__PURE__*/generateColorScale('info', '#2980b9', 5, 5), /*#__PURE__*/generateColorScale('warning', '#f1c40f', 5, 5));
function createDocsTheme(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$colors = _ref.colors,
      colors = _ref$colors === void 0 ? docsThemeColorPalette : _ref$colors,
      _ref$faces = _ref.faces,
      faces = _ref$faces === void 0 ? {} : _ref$faces,
      _ref$mixins = _ref.mixins,
      mixins = _ref$mixins === void 0 ? {} : _ref$mixins;

  return createNPointFontScaleTheme({
    borderRadius: {
      controlBorderRadius: 6,
      keyboardBorderRadius: 3
    },
    mixins: mixins,
    faces: faces,
    fontSize: {
      values: [10, 12, 14, 16, 20, 24, 28, 32, 40, 48],
      offset: 2
    },
    lineHeights: {
      values: [18, 20, 22, 24, 28, 32, 36, 40, 48, 56],
      offset: 2
    },
    baseGridSize: 8,
    fontFamily: {
      body: 'Open Sans,sans-serif',
      heading: 'Nunito Sans,sans-serif'
    },
    colors: colors
  });
}

/**
 * Creates a theme based on Npoint grid system
 */

function createNPointModularScaleTheme(settings) {
  var baseFontSize = settings.baseFontSize,
      baseGridSize = settings.baseGridSize,
      baseLineHeightRatio = settings.baseLineHeightRatio,
      fontScaleRatio = settings.fontScaleRatio;
  var modularScaleSettings = [];

  if (baseFontSize == null || Array.isArray(baseFontSize) && baseFontSize.length === 0) {
    throw new Error('Please set up baseFontSize');
  } // create responsive styles


  (typeof baseFontSize === 'number' ? [baseFontSize] : baseFontSize).forEach(function (fontSize) {
    var baseLineHeight = fontSize * baseLineHeightRatio;
    var alignedBaseLineHeight = Math.round(baseLineHeight / baseGridSize) * baseGridSize;
    var modularScale = ModularScale({
      base: fontSize,
      ratio: fontScaleRatio
    });
    modularScaleSettings.push({
      alignedBaseLineHeight: alignedBaseLineHeight,
      baseLineHeight: baseLineHeight,
      modularScale: modularScale
    });
  });
  return createTheme({
    resolvers: _extends({}, defaultResolvers, {
      /**
       * Resolver responsible for calculating line height sizes based on
       * modular scale and base line height
       */
      modularLineHeight: function modularLineHeight(propName, value, ctx) {
        var numericValue = Number(value);

        if (!Number.isNaN(numericValue)) {
          var _getResponsiveValue = getResponsiveValue(ctx.breakpoint, modularScaleSettings),
              alignedBaseLineHeight = _getResponsiveValue.alignedBaseLineHeight;

          var modularSize = ctx.resolve('modularSize', value, ctx);
          var lineHeightCoefficient = Math.ceil(modularSize / alignedBaseLineHeight);
          var alignedLineHeight = lineHeightCoefficient * alignedBaseLineHeight;
          return alignedLineHeight;
        }

        return value;
      },

      /**
       * Resolver responsible for calculating sizes based on modular scale
       */
      modularSize: function modularSize(propName, value, ctx) {
        var numericValue = Number(value);

        if (!Number.isNaN(numericValue)) {
          var _getResponsiveValue2 = getResponsiveValue(ctx.breakpoint, modularScaleSettings),
              modularScale = _getResponsiveValue2.modularScale;

          return modularScale(numericValue);
        }

        return value;
      }
    }),
    stylers: _extends({}, defaultStylers, {
      fontSize: {
        format: 'px',
        resolver: 'modularSize'
      },

      /**
       * Calculates fontSize and lineHeight for svg icons
       */
      iconSize: {
        format: 'px',
        resolver: 'modularLineHeight',
        outputProps: ['fontSize', 'lineHeight']
      },
      lineHeight: {
        format: 'px',
        resolver: 'modularLineHeight'
      },

      /**
       * Calculates a height based similarly to lineHeight
       */
      linedHeight: {
        format: 'px',
        resolver: 'modularLineHeight',
        outputProps: ['height']
      },
      linedWidth: {
        format: 'px',
        resolver: 'modularLineHeight',
        outputProps: ['width']
      }
    }),
    theme: settings
  });
}

export { createDocsTheme, createNPointModularScaleTheme, docsThemeColorPalette, findForegroundColor, generateColorScale };
//# sourceMappingURL=byteclaw.visage.themes.esm.js.map
